import { useRef } from 'react';

/**
 * One tap, one write: the synchronous lock for a write that goes out ON the tap (LESSONS
 * 2026-10-02). `disabled` and `isPending` cannot stop a double tap, because TanStack tells the
 * screen a write is pending through `setTimeout(0)`, so both taps land in the same render's
 * handler and each mints its own idempotency key, which the server cannot dedupe.
 *
 * The lock is taken BEFORE `send` mints the key and starts the write, and released when the
 * write settles (the caller's `onSettled` calls `release`). If `send` throws (minting the key
 * can fail before any mutation exists: storage or crypto refused), nothing would ever settle,
 * so the lock is released there and the error rethrown: TechVisitPage's `changeState` rule.
 *
 * The shared hook MAP 241 names. Ops insights 5a uses it throughout: the Tickets page, and the
 * tech's "Flag a problem" and "Tickets on this job". Phase 3 and 4's own locks
 * (`stateTappedAt` and its siblings) are not migrated yet.
 */
export interface TapOnce {
  /** Send under the lock: false (and nothing sent) while a write it started is out. */
  run(send: () => void): boolean;
  /** The write settled (landed or refused): the next tap may send. */
  release(): void;
}

export function createTapOnce(): TapOnce {
  let held = false;
  return {
    run(send) {
      if (held) return false;
      held = true;
      try {
        send();
      } catch (err) {
        held = false;
        throw err;
      }
      return true;
    },
    release() {
      held = false;
    },
  };
}

/** One lock per control, for the life of the component. */
export function useTapOnce(): TapOnce {
  const lock = useRef<TapOnce | null>(null);
  lock.current ??= createTapOnce();
  return lock.current;
}
